import React, { useState } from 'react';
import { Link } from 'react-router-dom';

function Protocol() {
  const [activeExample, setActiveExample] = useState('javascript');

  return (
    <div className="doc-page">
      {/* Navigation */}
      <nav className="nav">
        <div className="nav-container">
          <Link to="/" className="nav-logo">
            <span className="logo-text">AVALW</span>
            <span className="logo-badge">Worker</span>
          </Link>
          <div className="nav-links">
            <Link to="/transparency" className="nav-link">Transparency</Link>
            <Link to="/protocol" className="nav-link">Protocol</Link>
            <Link to="/security" className="nav-link">Security</Link>
            <Link to="/status" className="nav-link">Status</Link>
            <Link to="/dashboard" className="nav-btn">Dashboard</Link>
          </div>
        </div>
      </nav>

      <div className="doc-container">
        <div className="doc-header">
          <h1 className="doc-title">API Documentation</h1>
          <p className="doc-subtitle">
            Complete technical documentation of the Search API, heartbeat protocol, and integration examples.
          </p>
        </div>

        {/* Overview */}
        <section className="doc-section">
          <h2>Overview</h2>
          <p>The AVALW Worker API provides search functionality in exchange for compute contribution. All communications use HTTPS with JSON responses.</p>

          <table className="doc-table">
            <tbody>
              <tr>
                <td>Base URL</td>
                <td><code>https://worker.avalw.org/api</code></td>
              </tr>
              <tr>
                <td>Protocol</td>
                <td>HTTPS (TLS 1.2+)</td>
              </tr>
              <tr>
                <td>Format</td>
                <td>JSON</td>
              </tr>
              <tr>
                <td>Authentication</td>
                <td>Worker Token (Bearer or query param)</td>
              </tr>
              <tr>
                <td>Rate Limits</td>
                <td>Based on Core-Hours contribution tier</td>
              </tr>
            </tbody>
          </table>
        </section>

        {/* Distributed Cache System */}
        <section className="doc-section">
          <h2>Distributed Cache System</h2>
          <p>Workers serve as distributed cache nodes for the AVALW search network. When you run a worker, you contribute cache storage that helps speed up search responses for all users.</p>

          <div className="info-box success">
            <div className="info-box-title">How It Works</div>
            <ol style={{marginLeft: '1.5rem', marginTop: '0.5rem', lineHeight: '1.8'}}>
              <li>Your worker connects to the AVALW coordinator via WebSocket</li>
              <li>When users search on avalw.org, the system checks if any worker has the result cached</li>
              <li>If your worker has the result, you serve it directly - reducing server load</li>
              <li>New results are distributed to workers for caching</li>
              <li>You earn Core-Hours based on your contribution</li>
            </ol>
          </div>

          <h3>WebSocket Connection</h3>
          <table className="doc-table">
            <tbody>
              <tr>
                <td>WebSocket URL</td>
                <td><code>wss://worker.avalw.org/ws/cache</code></td>
              </tr>
              <tr>
                <td>Protocol</td>
                <td>JSON messages over WebSocket</td>
              </tr>
              <tr>
                <td>Cache TTL</td>
                <td>5 minutes default (configurable)</td>
              </tr>
              <tr>
                <td>Max Cache Size</td>
                <td>100MB - 2GB (configurable)</td>
              </tr>
              <tr>
                <td>Max Age</td>
                <td>7 days default (1-30 days configurable)</td>
              </tr>
            </tbody>
          </table>
        </section>

        {/* Intelligent Cache */}
        <section className="doc-section">
          <h2>Intelligent Cache System</h2>
          <p>Version 2.1.0 introduces smart caching with automatic management, popularity scoring, and configurable limits.</p>

          <h3>Cache Features</h3>
          <table className="doc-table">
            <thead>
              <tr>
                <th>Feature</th>
                <th>Description</th>
                <th>Default</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Size Limits</strong></td>
                <td>Maximum cache size in bytes. Oldest/least popular items evicted when full.</td>
                <td>500 MB</td>
              </tr>
              <tr>
                <td><strong>Time-Based Eviction</strong></td>
                <td>Queries not accessed for N days are automatically removed.</td>
                <td>7 days</td>
              </tr>
              <tr>
                <td><strong>Popularity Scoring</strong></td>
                <td>Frequently accessed queries stay cached longer. Score = access count × recency × stability.</td>
                <td>Auto</td>
              </tr>
              <tr>
                <td><strong>Auto-Cleanup</strong></td>
                <td>Background process periodically removes stale entries.</td>
                <td>Every 1 hour</td>
              </tr>
            </tbody>
          </table>

          <h3>CLI Options</h3>
          <div className="code-block">
            <div className="code-header">Command Line</div>
            <pre>{`# Default settings (500MB cache, 7 day retention)
npx @avalw/search-worker --token YOUR_TOKEN

# Custom cache size (100MB - 2048MB)
npx @avalw/search-worker --token YOUR_TOKEN --cache-size 1024

# Custom max age (1-30 days)
npx @avalw/search-worker --token YOUR_TOKEN --max-age 14

# Combined: 1GB cache with 14 day retention
npx @avalw/search-worker --token YOUR_TOKEN --cache-size 1024 --max-age 14`}</pre>
          </div>

          <h3>Environment Variables</h3>
          <table className="doc-table">
            <thead>
              <tr>
                <th>Variable</th>
                <th>Description</th>
                <th>Default</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>AVALW_WORKER_TOKEN</code></td>
                <td>Worker authentication token</td>
                <td>Required</td>
              </tr>
              <tr>
                <td><code>AVALW_CACHE_SIZE_MB</code></td>
                <td>Maximum cache size in MB (100-2048)</td>
                <td>500</td>
              </tr>
              <tr>
                <td><code>AVALW_MAX_AGE_DAYS</code></td>
                <td>Days to keep unused queries (1-30)</td>
                <td>7</td>
              </tr>
            </tbody>
          </table>

          <h3>Programmatic Configuration</h3>
          <div className="code-block">
            <div className="code-header">JavaScript</div>
            <pre>{`const { SearchWorker } = require('@avalw/search-worker');

const worker = new SearchWorker({
  workerToken: 'your_token',
  cacheSizeMB: 1024,       // 1GB cache
  maxAgeDays: 14,          // 14 day retention
  maxCacheEntries: 10000,  // Max entries fallback
  cleanupIntervalMs: 3600000  // Cleanup every hour
});

worker.connect();

// Runtime configuration
worker.setCacheSize(2048);   // Change to 2GB
worker.setCacheMaxAge(30);   // Change to 30 days

// Get cache stats
const stats = worker.getStats();
console.log(stats.cache);
// {
//   entries: 5234,
//   sizeBytes: 524288000,
//   sizeFormatted: "500 MB",
//   maxSizeFormatted: "1 GB",
//   utilizationPercent: 50,
//   evictions: 123,
//   timeEvictions: 45,
//   popularityEvictions: 78,
//   hitRate: 85
// }`}</pre>
          </div>

          <h3>Eviction Priority</h3>
          <p>When cache is full, items are evicted in this order:</p>
          <ol style={{marginLeft: '1.5rem', marginTop: '0.5rem', lineHeight: '1.8'}}>
            <li><strong>Expired TTL:</strong> Items past their TTL are removed first</li>
            <li><strong>Max Age:</strong> Items not accessed for N days (default 7)</li>
            <li><strong>Lowest Popularity:</strong> Least popular items (low access count, old last access)</li>
          </ol>

          <div className="info-box info">
            <div className="info-box-title">Popularity Score Formula</div>
            <p><code>Score = log2(accessCount + 1) × recencyFactor × stabilityFactor</code></p>
            <ul style={{marginTop: '0.5rem', marginLeft: '1rem', lineHeight: '1.8'}}>
              <li><strong>recencyFactor:</strong> 1.0 → 0.0 over 7 days since last access</li>
              <li><strong>stabilityFactor:</strong> Higher for items with consistent access over time</li>
            </ul>
          </div>
        </section>

        {/* WebSocket Protocol */}
        <section className="doc-section">
          <h2>WebSocket Protocol</h2>

          <h3>Message Types: Worker → Coordinator</h3>
          <div className="code-block">
            <div className="code-header">Worker → Coordinator</div>
            <pre>{`// Register cached keys on connect
{
  "type": "register_cache",
  "keys": ["query1:all:1:10", "query2:news:1:10"]
}

// Respond to cache request
{
  "type": "cache_response",
  "requestId": "abc123",
  "hit": true,
  "value": { /* search result */ }
}

// Notify about stored key
{
  "type": "cache_stored",
  "key": "spotify:all:1:10",
  "cacheStats": { "entries": 234, "sizeFormatted": "45 MB" }
}

// Notify about evicted key
{
  "type": "cache_evicted",
  "key": "old_query:all:1:10",
  "reason": "age"  // "ttl", "age", "popularity", "invalidate"
}

// Heartbeat with stats
{
  "type": "heartbeat",
  "cacheSize": 234,
  "stats": { "cacheHits": 150, "cacheMisses": 50 },
  "cpu_usage": 15.5
}`}</pre>
          </div>

          <h3>Message Types: Coordinator → Worker</h3>
          <div className="code-block">
            <div className="code-header">Coordinator → Worker</div>
            <pre>{`// Welcome message with config
{
  "type": "welcome",
  "workerId": "abc123...",
  "config": {
    "maxSizeMB": 500,
    "maxAgeDays": 7
  }
}

// Request cache lookup
{
  "type": "cache_get",
  "requestId": "def456",
  "key": "spotify:all:1:10"
}

// Store in cache
{
  "type": "cache_set",
  "key": "spotify:all:1:10",
  "value": { /* search result */ },
  "ttl": 300000
}

// Invalidate cache key
{
  "type": "cache_invalidate",
  "key": "spotify:all:1:10"
}

// Update configuration at runtime
{
  "type": "config_update",
  "maxSizeMB": 1024,
  "maxAgeDays": 14
}

// Ping (keepalive)
{
  "type": "ping"
}`}</pre>
          </div>
        </section>

        {/* Search API */}
        <section className="doc-section">
          <h2>Search API</h2>
          <p>The main endpoint for searching the AVALW index.</p>

          <h3>Endpoint</h3>
          <div className="code-block">
            <div className="code-header">HTTP</div>
            <pre>GET /api/search</pre>
          </div>

          <h3>Parameters</h3>
          <table className="doc-table">
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Type</th>
                <th>Required</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>q</code></td>
                <td>string</td>
                <td>Yes</td>
                <td>Search query (URL encoded)</td>
              </tr>
              <tr>
                <td><code>token</code></td>
                <td>string</td>
                <td>Yes*</td>
                <td>Worker token (alternative to Authorization header)</td>
              </tr>
              <tr>
                <td><code>limit</code></td>
                <td>integer</td>
                <td>No</td>
                <td>Max results (default: 10, max: 100)</td>
              </tr>
              <tr>
                <td><code>offset</code></td>
                <td>integer</td>
                <td>No</td>
                <td>Pagination offset (default: 0)</td>
              </tr>
            </tbody>
          </table>
          <p style={{fontSize: '0.875rem', color: '#64748b'}}>* Token can be passed via query param or Authorization header</p>

          <h3>Code Examples</h3>
          <div className="example-tabs">
            <button
              className={`tab-btn ${activeExample === 'javascript' ? 'active' : ''}`}
              onClick={() => setActiveExample('javascript')}
            >
              JavaScript
            </button>
            <button
              className={`tab-btn ${activeExample === 'python' ? 'active' : ''}`}
              onClick={() => setActiveExample('python')}
            >
              Python
            </button>
            <button
              className={`tab-btn ${activeExample === 'go' ? 'active' : ''}`}
              onClick={() => setActiveExample('go')}
            >
              Go
            </button>
            <button
              className={`tab-btn ${activeExample === 'rust' ? 'active' : ''}`}
              onClick={() => setActiveExample('rust')}
            >
              Rust
            </button>
            <button
              className={`tab-btn ${activeExample === 'curl' ? 'active' : ''}`}
              onClick={() => setActiveExample('curl')}
            >
              cURL
            </button>
          </div>

          {activeExample === 'javascript' && (
            <div className="code-block">
              <div className="code-header">JavaScript (Fetch)</div>
              <pre>{`const WORKER_TOKEN = 'your_worker_token';
const API_URL = 'https://worker.avalw.org/api';

async function search(query) {
  const response = await fetch(
    \`\${API_URL}/search?q=\${encodeURIComponent(query)}\`,
    {
      headers: {
        'Authorization': \`Bearer \${WORKER_TOKEN}\`
      }
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Search failed');
  }

  return response.json();
}

// Usage
search('web development')
  .then(data => {
    console.log(\`Found \${data.total} results\`);
    data.results.forEach(r => console.log(r.title));
  })
  .catch(err => console.error(err));`}</pre>
            </div>
          )}

          {activeExample === 'python' && (
            <div className="code-block">
              <div className="code-header">Python (requests)</div>
              <pre>{`import requests
from urllib.parse import quote

WORKER_TOKEN = 'your_worker_token'
API_URL = 'https://worker.avalw.org/api'

def search(query: str, limit: int = 10) -> dict:
    """Search the AVALW index."""
    response = requests.get(
        f"{API_URL}/search",
        params={
            'q': query,
            'limit': limit
        },
        headers={
            'Authorization': f'Bearer {WORKER_TOKEN}'
        }
    )

    response.raise_for_status()
    return response.json()

# Usage
if __name__ == '__main__':
    try:
        results = search('web development', limit=5)
        print(f"Found {results['total']} results")
        for r in results['results']:
            print(f"- {r['title']}: {r['url']}")
        print(f"Tier: {results['tier']}")
        print(f"Usage: {results['usage']['today']}/{results['usage']['limit']}")
    except requests.HTTPError as e:
        print(f"Error: {e.response.json().get('error', str(e))}")`}</pre>
            </div>
          )}

          {activeExample === 'go' && (
            <div className="code-block">
              <div className="code-header">Go</div>
              <pre>{`package main

import (
    "encoding/json"
    "fmt"
    "io"
    "net/http"
    "net/url"
)

const (
    WorkerToken = "your_worker_token"
    APIURL      = "https://worker.avalw.org/api"
)

type SearchResult struct {
    Title   string \`json:"title"\`
    URL     string \`json:"url"\`
    Domain  string \`json:"domain"\`
    Snippet string \`json:"snippet"\`
}

type SearchResponse struct {
    Success bool           \`json:"success"\`
    Results []SearchResult \`json:"results"\`
    Total   int            \`json:"total"\`
    Tier    string         \`json:"tier"\`
    Usage   struct {
        Today int \`json:"today"\`
        Limit int \`json:"limit"\`
    } \`json:"usage"\`
}

func Search(query string) (*SearchResponse, error) {
    reqURL := fmt.Sprintf("%s/search?q=%s", APIURL, url.QueryEscape(query))

    req, err := http.NewRequest("GET", reqURL, nil)
    if err != nil {
        return nil, err
    }

    req.Header.Set("Authorization", "Bearer "+WorkerToken)

    resp, err := http.DefaultClient.Do(req)
    if err != nil {
        return nil, err
    }
    defer resp.Body.Close()

    body, _ := io.ReadAll(resp.Body)

    var result SearchResponse
    if err := json.Unmarshal(body, &result); err != nil {
        return nil, err
    }

    return &result, nil
}

func main() {
    results, err := Search("web development")
    if err != nil {
        fmt.Printf("Error: %v\\n", err)
        return
    }

    fmt.Printf("Found %d results\\n", results.Total)
    for _, r := range results.Results {
        fmt.Printf("- %s: %s\\n", r.Title, r.URL)
    }
}`}</pre>
            </div>
          )}

          {activeExample === 'rust' && (
            <div className="code-block">
              <div className="code-header">Rust (reqwest)</div>
              <pre>{`use reqwest::header::{HeaderMap, AUTHORIZATION};
use serde::{Deserialize, Serialize};

const WORKER_TOKEN: &str = "your_worker_token";
const API_URL: &str = "https://worker.avalw.org/api";

#[derive(Debug, Deserialize)]
struct SearchResult {
    title: String,
    url: String,
    domain: String,
    snippet: String,
}

#[derive(Debug, Deserialize)]
struct Usage {
    today: u32,
    limit: u32,
}

#[derive(Debug, Deserialize)]
struct SearchResponse {
    success: bool,
    results: Vec<SearchResult>,
    total: u32,
    tier: String,
    usage: Usage,
}

async fn search(query: &str) -> Result<SearchResponse, reqwest::Error> {
    let client = reqwest::Client::new();

    let mut headers = HeaderMap::new();
    headers.insert(
        AUTHORIZATION,
        format!("Bearer {}", WORKER_TOKEN).parse().unwrap(),
    );

    let response = client
        .get(format!("{}/search", API_URL))
        .query(&[("q", query)])
        .headers(headers)
        .send()
        .await?
        .json::<SearchResponse>()
        .await?;

    Ok(response)
}

#[tokio::main]
async fn main() {
    match search("web development").await {
        Ok(data) => {
            println!("Found {} results", data.total);
            for r in data.results {
                println!("- {}: {}", r.title, r.url);
            }
        }
        Err(e) => eprintln!("Error: {}", e),
    }
}`}</pre>
            </div>
          )}

          {activeExample === 'curl' && (
            <div className="code-block">
              <div className="code-header">cURL</div>
              <pre>{`# With Authorization header
curl -X GET "https://worker.avalw.org/api/search?q=web+development" \\
  -H "Authorization: Bearer YOUR_WORKER_TOKEN"

# With query parameter
curl -X GET "https://worker.avalw.org/api/search?q=web+development&token=YOUR_WORKER_TOKEN"

# With pagination
curl -X GET "https://worker.avalw.org/api/search?q=web+development&limit=20&offset=0" \\
  -H "Authorization: Bearer YOUR_WORKER_TOKEN"`}</pre>
            </div>
          )}

          <h3>Response</h3>
          <div className="code-block">
            <div className="code-header">JSON Response</div>
            <pre>{`{
  "success": true,
  "results": [
    {
      "title": "Example Page Title",
      "url": "https://example.com/page",
      "domain": "example.com",
      "snippet": "This is a description of the page content..."
    }
  ],
  "total": 150,
  "searchTime": 45,
  "tier": "Basic",
  "usage": {
    "today": 235,
    "limit": 5000,
    "core_hours": 8.5
  }
}`}</pre>
          </div>
        </section>

        {/* Error Handling */}
        <section className="doc-section">
          <h2>Error Handling</h2>
          <p>All errors return a JSON object with an <code>error</code> field and appropriate HTTP status code.</p>

          <h3>Error Response Format</h3>
          <div className="code-block">
            <div className="code-header">JSON</div>
            <pre>{`{
  "error": "Error description",
  "message": "Detailed message (optional)",
  "code": 400
}`}</pre>
          </div>

          <h3>Error Codes</h3>
          <table className="doc-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Error</th>
                <th>Cause</th>
                <th>Solution</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>400</code></td>
                <td>Missing query parameter</td>
                <td>No <code>q</code> parameter provided</td>
                <td>Add search query to request</td>
              </tr>
              <tr>
                <td><code>401</code></td>
                <td>Missing/Invalid token</td>
                <td>No token or invalid token format</td>
                <td>Check token in dashboard</td>
              </tr>
              <tr>
                <td><code>403</code></td>
                <td>Worker offline</td>
                <td>Worker must be running to use API</td>
                <td>Start worker: <code>avalw-worker TOKEN</code></td>
              </tr>
              <tr>
                <td><code>403</code></td>
                <td>Domain not whitelisted</td>
                <td>Request origin not in whitelist</td>
                <td>Add domain in dashboard</td>
              </tr>
              <tr>
                <td><code>403</code></td>
                <td>Insufficient cores</td>
                <td>System has fewer than 3 cores</td>
                <td>Minimum 3 CPU cores required</td>
              </tr>
              <tr>
                <td><code>403</code></td>
                <td>Insufficient contribution</td>
                <td>Not enough core-hours accumulated</td>
                <td>Keep worker running longer</td>
              </tr>
              <tr>
                <td><code>429</code></td>
                <td>Daily limit reached</td>
                <td>Exceeded tier's daily requests</td>
                <td>Wait for midnight UTC reset or increase contribution</td>
              </tr>
              <tr>
                <td><code>500</code></td>
                <td>Server error</td>
                <td>Internal server issue</td>
                <td>Retry later, check /status</td>
              </tr>
            </tbody>
          </table>

          <h3>Error Handling Example</h3>
          <div className="code-block">
            <div className="code-header">JavaScript</div>
            <pre>{`async function searchWithErrorHandling(query) {
  try {
    const response = await fetch(\`\${API_URL}/search?q=\${query}\`, {
      headers: { 'Authorization': \`Bearer \${TOKEN}\` }
    });

    const data = await response.json();

    if (!response.ok) {
      switch (response.status) {
        case 403:
          if (data.error === 'Worker offline') {
            console.error('Start your worker first!');
          } else if (data.error === 'Domain not whitelisted') {
            console.error('Add this domain to your whitelist');
          }
          break;
        case 429:
          console.error(\`Rate limited. Resets at midnight UTC. Used: \${data.used}/\${data.limit}\`);
          break;
        default:
          console.error(\`API Error: \${data.error}\`);
      }
      return null;
    }

    return data;
  } catch (err) {
    console.error('Network error:', err);
    return null;
  }
}`}</pre>
          </div>
        </section>

        {/* Heartbeat Protocol */}
        <section className="doc-section">
          <h2>Heartbeat Protocol</h2>
          <p>Workers send heartbeats every 30 seconds to report status and accumulate core-hours.</p>

          <h3>Endpoint</h3>
          <div className="code-block">
            <div className="code-header">HTTP</div>
            <pre>POST /api/worker/heartbeat</pre>
          </div>

          <h3>Request Body</h3>
          <div className="code-block">
            <div className="code-header">JSON</div>
            <pre>{`{
  "worker_token": "your_96_char_hex_token",
  "system_info": {
    "platform": "win32",
    "arch": "x64",
    "cores": 8,
    "cpu_model": "Intel(R) Core(TM) i7-12700K",
    "cpu_speed_ghz": 3.6,
    "speed_factor": 1.2,
    "total_memory_gb": 32,
    "free_memory_gb": 18,
    "cpu_usage": 45.5,
    "memory_usage": 62.3,
    "session_core_hours": 2.5,
    "worker_uptime_hours": 3.2
  }
}`}</pre>
          </div>

          <h3>Response</h3>
          <div className="code-block">
            <div className="code-header">JSON</div>
            <pre>{`{
  "success": true,
  "core_hours_today": 8.5,
  "tier": {
    "key": "BASIC",
    "name": "Basic",
    "coreHours": 12,
    "requests": 5000,
    "minCores": 4
  },
  "next_tier": {
    "key": "PRO",
    "name": "Pro",
    "coreHours": 36,
    "requests": 20000,
    "minCores": 6
  },
  "usage_today": 234,
  "usage_limit": 5000,
  "next_heartbeat": 30000
}`}</pre>
          </div>
        </section>

        {/* Other Endpoints */}
        <section className="doc-section">
          <h2>Other Endpoints</h2>

          <h3>Register Account</h3>
          <div className="code-block">
            <div className="code-header">HTTP</div>
            <pre>{`POST /api/register

Response:
{
  "success": true,
  "account_hash": "sha256_hash_64_chars",
  "worker_token": "96_char_hex_token",
  "message": "Save your account_hash to log in later"
}`}</pre>
          </div>

          <h3>Login</h3>
          <div className="code-block">
            <div className="code-header">HTTP</div>
            <pre>{`POST /api/login
Body: { "account_hash": "your_account_hash" }

Response: Account details, tier, usage stats`}</pre>
          </div>

          <h3>Worker Status</h3>
          <div className="code-block">
            <div className="code-header">HTTP</div>
            <pre>{`GET /api/worker/status/:token

Response: Worker status, system info, core-hours, tier`}</pre>
          </div>

          <h3>System Status</h3>
          <div className="code-block">
            <div className="code-header">HTTP</div>
            <pre>{`GET /api/status

Response: System status, uptime, active workers, incidents`}</pre>
          </div>

          <h3>Whitelist Domain</h3>
          <div className="code-block">
            <div className="code-header">HTTP</div>
            <pre>{`POST /api/whitelist
Body: { "account_hash": "...", "domain": "example.com" }

DELETE /api/whitelist
Body: { "account_hash": "...", "domain": "example.com" }`}</pre>
          </div>

          <h3>Token Management</h3>
          <div className="code-block">
            <div className="code-header">HTTP</div>
            <pre>{`# List tokens
GET /api/tokens/:account_hash

# Create token
POST /api/tokens/:account_hash
Body: { "name": "My Token", "permissions": ["search"], "expires_in_days": 30 }

# Revoke token
DELETE /api/tokens/:account_hash/:token_id

# Regenerate primary token
POST /api/tokens/:account_hash/regenerate`}</pre>
          </div>
        </section>

        {/* Rate Limits */}
        <section className="doc-section">
          <h2>Rate Limits & Tiers</h2>
          <p>Rate limits are based on your Core-Hours contribution. More contribution = higher limits.</p>

          <table className="doc-table">
            <thead>
              <tr>
                <th>Tier</th>
                <th>Core-Hours Required</th>
                <th>Min Cores</th>
                <th>Requests/Day</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Starter</td>
                <td>3 CH</td>
                <td>3</td>
                <td>1,000</td>
              </tr>
              <tr>
                <td>Basic</td>
                <td>12 CH</td>
                <td>4</td>
                <td>5,000</td>
              </tr>
              <tr>
                <td>Pro</td>
                <td>36 CH</td>
                <td>6</td>
                <td>20,000</td>
              </tr>
              <tr>
                <td>Business</td>
                <td>72 CH</td>
                <td>8</td>
                <td>50,000</td>
              </tr>
              <tr>
                <td>Enterprise</td>
                <td>240 CH</td>
                <td>16</td>
                <td>500,000</td>
              </tr>
            </tbody>
          </table>

          <div className="info-box info">
            <div className="info-box-title">Core-Hours Formula</div>
            <p><code>Core-Hours = Cores x (Speed / 3.0 GHz) x Usage% x Hours</code></p>
            <p style={{marginTop: '0.5rem', fontSize: '0.875rem'}}>
              Example: 8 cores at 3.6 GHz (1.2x factor) running for 2 hours = 8 x 1.2 x 0.5 x 2 = 9.6 Core-Hours
            </p>
          </div>
        </section>

        {/* Back Link */}
        <div style={{ marginTop: '3rem', paddingTop: '2rem', borderTop: '1px solid #e2e8f0' }}>
          <Link to="/" className="btn btn-outline">Back to Home</Link>
        </div>
      </div>
    </div>
  );
}

export default Protocol;
